import { create } from "zustand";
import { getSnapshotTimestamps } from "@/api/snapshots";
import {
  SnapshotDay,
  SnapshotSlotKey,
  SnapshotTimestampsResponse,
  SNAPSHOT_SLOT_DEFINITIONS,
} from "@/types/snapshots";

type SnapshotCache = Partial<Record<SnapshotDay, SnapshotTimestampsResponse>>;

interface SnapshotsState {
  selectedDay: SnapshotDay;
  selectedSlot: SnapshotSlotKey;
  timestampsByDay: SnapshotCache;
  isLoading: boolean;
  activeSnapshotTimestamp: string | null;
  initialize: () => Promise<void>;
  setSelectedDay: (day: SnapshotDay) => Promise<void>;
  setSelectedSlot: (slot: SnapshotSlotKey) => void;
}

function getLatestAvailableSlot(
  timestamps: SnapshotTimestampsResponse | undefined
): SnapshotSlotKey | null {
  if (!timestamps) return null;

  for (let i = SNAPSHOT_SLOT_DEFINITIONS.length - 1; i >= 0; i -= 1) {
    const slotKey = SNAPSHOT_SLOT_DEFINITIONS[i].key;
    if (timestamps[slotKey]?.snapshot_timestamp) {
      return slotKey;
    }
  }

  return null;
}

function resolveSnapshotTimestamp(
  timestampsByDay: SnapshotCache,
  day: SnapshotDay,
  slot: SnapshotSlotKey
): string | null {
  return timestampsByDay[day]?.[slot]?.snapshot_timestamp ?? null;
}

async function loadDay(day: SnapshotDay): Promise<SnapshotTimestampsResponse> {
  return getSnapshotTimestamps(day);
}

export const useSnapshotsStore = create<SnapshotsState>()((set, get) => ({
  selectedDay: "today",
  selectedSlot: "19_24",
  timestampsByDay: {},
  isLoading: false,
  activeSnapshotTimestamp: null,
  initialize: async () => {
    const { timestampsByDay } = get();
    if (timestampsByDay.today && timestampsByDay.yesterday) {
      return;
    }

    set({ isLoading: true });

    try {
      const [today, yesterday] = await Promise.all([
        timestampsByDay.today
          ? Promise.resolve(timestampsByDay.today)
          : loadDay("today"),
        timestampsByDay.yesterday
          ? Promise.resolve(timestampsByDay.yesterday)
          : loadDay("yesterday"),
      ]);

      const preferredDay: SnapshotDay =
        getLatestAvailableSlot(today) !== null ? "today" : "yesterday";
      const preferredSlot =
        getLatestAvailableSlot(preferredDay === "today" ? today : yesterday) ??
        "19_24";

      set({
        timestampsByDay: {
          today,
          yesterday,
        },
        selectedDay: preferredDay,
        selectedSlot: preferredSlot,
        activeSnapshotTimestamp:
          (preferredDay === "today" ? today : yesterday)[preferredSlot]
            ?.snapshot_timestamp ?? null,
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  setSelectedDay: async (day) => {
    const { timestampsByDay, selectedSlot } = get();

    set({ isLoading: !timestampsByDay[day] });

    try {
      const nextDayTimestamps = timestampsByDay[day] ?? (await loadDay(day));
      const nextTimestampsByDay = {
        ...timestampsByDay,
        [day]: nextDayTimestamps,
      };
      const nextSlot =
        nextDayTimestamps[selectedSlot]?.snapshot_timestamp != null
          ? selectedSlot
          : getLatestAvailableSlot(nextDayTimestamps) ?? selectedSlot;

      set({
        selectedDay: day,
        selectedSlot: nextSlot,
        timestampsByDay: nextTimestampsByDay,
        activeSnapshotTimestamp: resolveSnapshotTimestamp(
          nextTimestampsByDay,
          day,
          nextSlot
        ),
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
    }
  },
  setSelectedSlot: (slot) => {
    const { selectedDay, timestampsByDay } = get();
    const snapshotTimestamp = resolveSnapshotTimestamp(
      timestampsByDay,
      selectedDay,
      slot
    );

    if (!snapshotTimestamp) {
      return;
    }

    set({
      selectedSlot: slot,
      activeSnapshotTimestamp: snapshotTimestamp,
    });
  },
}));
